import { safeLocalStorage } from '@toss/storage';

import { type AiBuilderQuestionSetResponse } from '../store/aiBuilderQuestionSetState';
import {
  type AiBuilderChatState,
  type AiBuilderMessage,
  type AiBuilderPendingQuestionSet,
  type AiBuilderPendingQuestionSetOption,
  type AiBuilderPendingQuestionSetQuestion,
  type AiBuilderUsageInfo,
} from '../store/types';

import { type AiBuilderChatSnapshotSkipReason } from './events.quiz-ai-builder';
import { isRecord } from './isRecord';

/**
 * 로그인 만료로 채팅이 끊긴 시점의 대화를 재로그인 후 되살리기 위한 스냅샷 저장소.
 *
 * 서버 `/conversation` 에는 대화 조회 GET 이 없어(POST session/turn/reset/source,
 * PUT feedback 이 전부) `session_id` 만 되살리면 LLM 문맥만 복구되고 화면은 빈
 * 채팅이 된다. 그래서 클라이언트가 자신의 메시지 배열을 그대로 들고 간다.
 *
 * 문항 초안(`quiz_build_v4`)과는 **별도 키**라 서로 밀어내지 않는다. 다만 복원은
 * 문항 복원("이어 만들기")과 결합해야 한다 — 턴 요청이 매번 폼의 questions 를
 * 실어보내므로, 대화만 복원되면 "문항 0개 + 10문제 만들었다는 이력" 불일치가 된다.
 */
const STORAGE_KEY = 'ai_builder_chat_restore_v1';
/**
 * v3 — 인증 만료 시 서버에 남아 있는 pending Question Set과 작성 답변을 보존한다.
 * v2 — id 카운터(`nextRequestId`·`nextRunId`)를 추가했다. 카운터 없는 v1 을 복원하면
 * 새 메시지 id 가 `user-1` 부터 다시 매겨져 복원된 메시지와 충돌하므로 버린다.
 */
const SNAPSHOT_VERSION = 'v3';

/**
 * 스냅샷 유효 기간. 서버 세션 TTL(7일)보다 훨씬 짧게 잡는다 — 로그인하지 않고
 * 며칠 뒤 빌더에 들어왔을 때 낡은 대화가 되살아나는 편이 더 혼란스럽다.
 */
const SNAPSHOT_TTL_MS = 60 * 60 * 1000;

export type AiBuilderChatRestoreSnapshot = {
  version: string;
  savedAt: number;
  /** 저장 시점 pathname. 다른 퀴즈 화면으로 복귀하면 복원하지 않는다. */
  path: string;
  sessionId: string;
  /**
   * 서버 세션 바인딩의 일부. 이 값이 빠지면 복원된 세션의 모든 요청이 403 이다
   * (서버가 draft_instance_id 를 포함해 바인딩을 통째로 비교한다).
   */
  draftInstanceId: string;
  messages: AiBuilderMessage[];
  usage: AiBuilderUsageInfo | null;
  sourceRefs: string[];
  draft: string;
  pendingQuestionSet: AiBuilderPendingQuestionSet | null;
  /**
   * 복원 후 발급될 첫 requestId. 메시지 id(`user-N`·`user-choice-N`·`user-confirm-N`)
   * 는 이 카운터로 만들어지는데, 재로그인은 hard navigation 이라 Provider 가 새로
   * 마운트되며 카운터가 0 으로 돌아간다. 시드하지 않으면 복원 직후 첫 메시지가
   * 복원된 `user-1` 과 같은 id 를 갖는다 — React key 중복, 상태 일괄 변경,
   * 재시도가 옛 메시지를 집는 문제가 한꺼번에 터진다.
   */
  nextRequestId: number;
  /** 복원 후 발급될 첫 runId. `source-preparation-N` 진행 말풍선 id 축. */
  nextRunId: number;
};

type SaveSnapshotParams = {
  state: AiBuilderChatState;
  draftInstanceId: string;
  path: string;
  now: number;
  nextRequestId: number;
  nextRunId: number;
};

/**
 * 재로그인 유도 모달을 띄우기 직전에 1회 호출한다.
 *
 * 저장 실패(용량 초과 등)는 삼킨다 — 대화 복원은 부가 기능이고, 저장이 실패했다고
 * 재로그인 안내 자체가 막히면 안 된다.
 *
 * `sessionId` 가 없는 만료(세션 생성 요청 자체가 401)는 **의도적으로 저장하지 않는다**.
 * 서버 세션이 없으면 복원해도 LLM 문맥이 비어 화면의 대화 이력과 어긋나고, 첫 턴 전
 * 시점이라 잃는 것도 사실상 없다. 재로그인 안내만 태우고 스냅샷은 남기지 않는다.
 */
export const saveAiBuilderChatRestoreSnapshot = ({
  state,
  draftInstanceId,
  path,
  now,
  nextRequestId,
  nextRunId,
}: SaveSnapshotParams): AiBuilderChatSnapshotSkipReason => {
  if (!state.sessionId) return 'no_session';
  if (state.messages.length === 0) return 'no_message';

  const snapshot: AiBuilderChatRestoreSnapshot = {
    version: SNAPSHOT_VERSION,
    savedAt: now,
    path,
    sessionId: state.sessionId,
    draftInstanceId,
    messages: state.messages,
    usage: state.usage,
    sourceRefs: state.sourceRefs,
    draft: state.draft,
    pendingQuestionSet: state.pendingQuestionSet ?? null,
    nextRequestId,
    nextRunId,
  };

  try {
    safeLocalStorage.set(STORAGE_KEY, JSON.stringify(snapshot));
    return '';
  } catch {
    // QuotaExceededError 등 — 복원을 포기하고 재로그인 흐름은 그대로 진행한다.
    return 'storage_error';
  }
};

export const clearAiBuilderChatRestoreSnapshot = () => {
  safeLocalStorage.remove(STORAGE_KEY);
};

/**
 * 저장·복원 일치 판정에 쓰는 현재 경로.
 *
 * AI 빌더 채팅은 `/build` 전용이 아니라 `/update/[id]` · `/duplicate/[id]` 에서도
 * 뜬다. 경로가 다르면 다른 퀴즈를 가리키는 대화이므로 복원하지 않는다.
 */
export const resolveAiBuilderChatRestorePath = (): string => {
  if (typeof window === 'undefined') return '';

  return `${window.location.pathname}${window.location.search}`;
};

/**
 * 스냅샷을 읽고 **탈락 사유까지** 알려준다. 저장소는 건드리지 않는다.
 *
 * 사유가 필요한 이유는 계측이다 — TTL(1시간)을 넘겨 조용히 버려진 대화가 얼마나
 * 되는지 모르면 TTL 이 적절한지 판단할 수 없다. `stale` 은 savedAt 을 읽을 수
 * 있으므로 스냅샷을 함께 실어 보낸다.
 */
export type AiBuilderChatRestoreReadResult =
  | { status: 'ready'; snapshot: AiBuilderChatRestoreSnapshot }
  /** 저장된 것이 없다 — 평시 진입. */
  | { status: 'absent' }
  /** 다른 화면의 대화다. 그 경로로 돌아오면 아직 유효하므로 지우지 않는다. */
  | { status: 'path_mismatch' }
  | { status: 'stale'; snapshot: AiBuilderChatRestoreSnapshot }
  /** 파싱 불가 또는 버전 불일치 — 되살릴 방법이 없다. */
  | { status: 'invalid' };

export const readAiBuilderChatRestoreSnapshot = ({
  path,
  now,
}: {
  path: string;
  now: number;
}): AiBuilderChatRestoreReadResult => {
  const raw = safeLocalStorage.get(STORAGE_KEY);
  if (!raw) return { status: 'absent' };

  const snapshot = parseSnapshot(raw);
  if (!snapshot) return { status: 'invalid' };
  if (snapshot.version !== SNAPSHOT_VERSION) return { status: 'invalid' };
  if (now - snapshot.savedAt > SNAPSHOT_TTL_MS) {
    return { status: 'stale', snapshot };
  }
  if (snapshot.path !== path) return { status: 'path_mismatch' };

  return { status: 'ready', snapshot };
};

/**
 * 복원 가능한 스냅샷을 읽되 **저장소에서는 지우지 않는다**.
 *
 * 세션 생성 보류 판단(§복원 결정 전 경합 방지)처럼 소비 없이 존재만 확인해야 하는
 * 곳이 있어 읽기와 소비를 분리한다.
 */
export const peekAiBuilderChatRestoreSnapshot = (params: {
  path: string;
  now: number;
}): AiBuilderChatRestoreSnapshot | null => {
  const result = readAiBuilderChatRestoreSnapshot(params);

  return result.status === 'ready' ? result.snapshot : null;
};

/**
 * 읽기 → 검증 → 삭제. 삭제는 복원 성공 여부와 무관하게 실행해 무한 복원 루프를 막는다.
 */
export const consumeAiBuilderChatRestoreSnapshot = (params: {
  path: string;
  now: number;
}): AiBuilderChatRestoreSnapshot | null => {
  const snapshot = peekAiBuilderChatRestoreSnapshot(params);
  clearAiBuilderChatRestoreSnapshot();
  return snapshot;
};

/**
 * 스냅샷을 채팅 상태로 되돌린다.
 *
 * 진행 중 플래그는 반드시 초기화한다 — `turnInProgress: true` 로 복원되면 입력창이
 * 영구히 잠긴다. 파일 핸들(`selectedFile`)은 직렬화 대상이 아니므로 비운다.
 */
export const applyAiBuilderChatRestoreSnapshot = (
  prev: AiBuilderChatState,
  snapshot: AiBuilderChatRestoreSnapshot,
): AiBuilderChatState => ({
  ...prev,
  panelOpen: true,
  sessionId: snapshot.sessionId,
  messages: normalizeRestoredMessages(snapshot.messages),
  usage: snapshot.usage,
  sourceRefs: snapshot.sourceRefs,
  draft: snapshot.draft,
  pendingQuestionSet: snapshot.pendingQuestionSet
    ? { ...snapshot.pendingQuestionSet, submissionError: null }
    : null,
  selectedFile: null,
  selectedQuizContext: null,
  isGenerating: false,
  turnInProgress: false,
});

const normalizeRestoredMessages = (
  messages: AiBuilderMessage[],
): AiBuilderMessage[] =>
  messages.map(message =>
    message.kind === 'user' && message.retry?.type === 'questionSet'
      ? { ...message, status: 'failed', retry: undefined }
      : message,
  );

const parseSnapshot = (raw: string): AiBuilderChatRestoreSnapshot | null => {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return null;
    if (typeof parsed['version'] !== 'string') return null;
    if (typeof parsed['savedAt'] !== 'number') return null;
    if (typeof parsed['path'] !== 'string') return null;
    if (typeof parsed['sessionId'] !== 'string' || !parsed['sessionId']) {
      return null;
    }
    if (
      typeof parsed['draftInstanceId'] !== 'string' ||
      !parsed['draftInstanceId']
    ) {
      return null;
    }
    if (!Array.isArray(parsed['messages'])) return null;
    if (!Array.isArray(parsed['sourceRefs'])) return null;
    if (typeof parsed['nextRequestId'] !== 'number') return null;
    if (typeof parsed['nextRunId'] !== 'number') return null;
    const pendingQuestionSet = parsePendingQuestionSet(
      parsed['pendingQuestionSet'],
    );
    if (pendingQuestionSet === undefined) return null;

    return {
      version: parsed['version'],
      savedAt: parsed['savedAt'],
      path: parsed['path'],
      sessionId: parsed['sessionId'],
      draftInstanceId: parsed['draftInstanceId'],
      messages: parsed['messages'] as AiBuilderMessage[],
      usage: (parsed['usage'] ?? null) as AiBuilderUsageInfo | null,
      sourceRefs: parsed['sourceRefs'] as string[],
      draft: typeof parsed['draft'] === 'string' ? parsed['draft'] : '',
      pendingQuestionSet,
      nextRequestId: parsed['nextRequestId'],
      nextRunId: parsed['nextRunId'],
    };
  } catch {
    return null;
  }
};

const parsePendingQuestionSet = (
  value: unknown,
): AiBuilderPendingQuestionSet | null | undefined => {
  if (value === null) return null;
  if (
    !isRecord(value) ||
    typeof value['interactionId'] !== 'string' ||
    typeof value['activeQuestionId'] !== 'string' ||
    !Array.isArray(value['questions']) ||
    value['questions'].length === 0 ||
    !isRecord(value['responsesByQuestionId']) ||
    typeof value['cancelled'] !== 'boolean' ||
    (value['submissionError'] !== null &&
      typeof value['submissionError'] !== 'string') ||
    typeof value['composerDraftSnapshot'] !== 'string'
  ) {
    return undefined;
  }

  const questions = value['questions'].map(parsePendingQuestion);
  if (questions.some(question => question === null)) return undefined;
  const parsedQuestions = questions.filter(
    (question): question is AiBuilderPendingQuestionSetQuestion =>
      question !== null,
  );
  if (
    !parsedQuestions.some(
      question => question.questionId === value['activeQuestionId'],
    )
  ) {
    return undefined;
  }

  const responsesByQuestionId: Record<string, AiBuilderQuestionSetResponse> =
    {};
  for (const [questionId, response] of Object.entries(
    value['responsesByQuestionId'],
  )) {
    const parsedResponse = parseQuestionSetResponse(response);
    if (
      !parsedResponse ||
      !parsedQuestions.some(question => question.questionId === questionId)
    ) {
      return undefined;
    }
    responsesByQuestionId[questionId] = parsedResponse;
  }

  return {
    interactionId: value['interactionId'],
    activeQuestionId: value['activeQuestionId'],
    questions: parsedQuestions,
    responsesByQuestionId,
    cancelled: value['cancelled'],
    submissionError: value['submissionError'],
    composerDraftSnapshot: value['composerDraftSnapshot'],
  };
};

const parsePendingQuestion = (
  value: unknown,
): AiBuilderPendingQuestionSetQuestion | null => {
  if (
    !isRecord(value) ||
    typeof value['questionId'] !== 'string' ||
    typeof value['question'] !== 'string' ||
    typeof value['context'] !== 'string' ||
    !Array.isArray(value['options']) ||
    (value['selectionMode'] !== 'single' &&
      value['selectionMode'] !== 'multiple') ||
    typeof value['allowDirectInput'] !== 'boolean' ||
    typeof value['missingSlot'] !== 'string'
  ) {
    return null;
  }
  const options = value['options'].map(parsePendingQuestionOption);
  if (options.some(option => option === null)) return null;

  return {
    questionId: value['questionId'],
    question: value['question'],
    context: value['context'],
    options: options.filter(
      (option): option is AiBuilderPendingQuestionSetOption => option !== null,
    ),
    selectionMode: value['selectionMode'],
    allowDirectInput: value['allowDirectInput'],
    missingSlot: value['missingSlot'],
  };
};

const parsePendingQuestionOption = (
  value: unknown,
): AiBuilderPendingQuestionSetOption | null => {
  if (
    !isRecord(value) ||
    typeof value['optionId'] !== 'string' ||
    typeof value['label'] !== 'string' ||
    (value['description'] !== null &&
      typeof value['description'] !== 'string') ||
    (value['value'] !== null && typeof value['value'] !== 'string')
  ) {
    return null;
  }
  return {
    optionId: value['optionId'],
    label: value['label'],
    description: value['description'],
    value: value['value'],
  };
};

const parseQuestionSetResponse = (
  value: unknown,
): AiBuilderQuestionSetResponse | null => {
  if (
    !isRecord(value) ||
    !Array.isArray(value['choiceOptionIds']) ||
    !value['choiceOptionIds'].every(optionId => typeof optionId === 'string') ||
    typeof value['text'] !== 'string' ||
    typeof value['directInputSelected'] !== 'boolean'
  ) {
    return null;
  }
  return {
    choiceOptionIds: value['choiceOptionIds'],
    text: value['text'],
    directInputSelected: value['directInputSelected'],
  };
};
